import irmLogo from 'assets/images/logoIRM.png';

const TEAL = '#00798E';

// Strips prefixes like IRMP, IRMCPL, IRMP-O-, IRMPCPL-O- etc. and keeps only
// the trailing 12-digit number so the barcode encodes a clean numeric value.
function barcodeValue(id) {
  return (id || '').replace(/\D/g, '').slice(-12);
}

/**
 * Opens a print-ready PVC (CR80) identity card for a patient, and their
 * spouse when present. Used by both Old Patient Registration (on success)
 * and Patient List ("Print Patient Card") so the two stay visually identical.
 *
 * Expected record shape:
 *   patientExternalCode, coupleExternalCode, spouseExternalCode,
 *   first_name, last_name, age, gender, mobile,
 *   spouse_first_name, spouse_last_name, spouse_age, spouse_gender, spouse_mobile
 */
export default function printPatientCard(record) {
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  const issuedBy = u.name || u.user_name || '';
  const issuedAt = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const patientId = record.patientExternalCode;
  const coupleId = record.coupleExternalCode;
  const spouseId = record.spouseExternalCode || '';

  const patientName = [record.first_name, record.last_name].filter(Boolean).join(' ');
  const spouseName = record.spouse_first_name
    ? [record.spouse_first_name, record.spouse_last_name].filter(Boolean).join(' ')
    : '';

  const genderStr = (g) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : 'Other');
  const hasSpouse = !!(spouseId && spouseName);

  const barcodeInits = [
    `JsBarcode('#bc-p1-pid','${barcodeValue(patientId)}',{format:'CODE128',width:1,height:22,displayValue:true,fontSize:6,margin:1});`,
    `JsBarcode('#bc-p1-cid','${barcodeValue(coupleId)}',{format:'CODE128',width:1,height:22,displayValue:true,fontSize:6,margin:1});`,
    hasSpouse ? `JsBarcode('#bc-p2-sid','${barcodeValue(spouseId)}',{format:'CODE128',width:1,height:22,displayValue:true,fontSize:6,margin:1});` : '',
    hasSpouse ? `JsBarcode('#bc-p2-cid','${barcodeValue(coupleId)}',{format:'CODE128',width:1,height:22,displayValue:true,fontSize:6,margin:1});` : '',
  ].filter(Boolean).join('\n');

  const barcodeScript = [
    '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>',
    '<script>window.onload=function(){',
    barcodeInits,
    '};</script>',
  ].join('\n');

  // Returns HTML string for one PVC card (CR80: 85.6mm × 54mm)
  const makeCard = ({ name, age, gender, mobile, idLabel, primaryId, cplId, bcPrimId, bcCplId, subtitle }) => `
    <div class="pvc-card">
      <div class="card-hdr">
        <img src="${irmLogo}" class="logo" alt="IRM"/>
        <div class="hdr-txt">
          <div class="clinic-name">IRM Fertility Centre</div>
          <div class="card-sub">${subtitle}</div>
        </div>
      </div>
      <div class="card-body">
        <div class="info-col">
          <div class="pt-name">${name}</div>
          <div class="info-row"><span class="lbl">Age</span><span class="val">${age ? age + ' Yrs' : '—'}</span></div>
          <div class="info-row"><span class="lbl">Gender</span><span class="val">${gender}</span></div>
          ${mobile ? `<div class="info-row"><span class="lbl">Mobile</span><span class="val">${mobile}</span></div>` : ''}
          <div class="info-row"><span class="lbl">${idLabel}</span><span class="val id-val">${primaryId}</span></div>
          <div class="info-row"><span class="lbl">Couple ID</span><span class="val id-val">${cplId}</span></div>
        </div>
        <div class="bc-col">
          <div class="bc-item">
            <div class="bc-lbl">${idLabel}</div>
            <svg id="${bcPrimId}"></svg>
          </div>
          <div class="bc-item">
            <div class="bc-lbl">Couple ID</div>
            <svg id="${bcCplId}"></svg>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <span>Issued: ${issuedAt}</span>
        <span>${issuedBy}</span>
      </div>
    </div>`;

  const patientCard = makeCard({
    name: patientName,
    age: record.age,
    gender: genderStr(record.gender),
    mobile: record.mobile,
    idLabel: 'Patient ID',
    primaryId: patientId,
    cplId: coupleId,
    bcPrimId: 'bc-p1-pid',
    bcCplId: 'bc-p1-cid',
    subtitle: 'Patient Identity Card',
  });

  const spouseCard = hasSpouse ? makeCard({
    name: spouseName,
    age: record.spouse_age,
    gender: genderStr(record.spouse_gender),
    mobile: record.spouse_mobile,
    idLabel: 'Spouse ID',
    primaryId: spouseId,
    cplId: coupleId,
    bcPrimId: 'bc-p2-sid',
    bcCplId: 'bc-p2-cid',
    subtitle: 'Spouse Identity Card',
  }) : '';

  const pageW = hasSpouse ? '198mm' : '102mm';

  const cardHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>Patient Card — ${patientId}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #e8e8e8; display: flex; flex-direction: column; align-items: center; padding-top: 52px; }
  @page { size: ${pageW} 70mm; margin: 5mm; }
  @media print { body { background: #fff; padding-top: 0; } #ptb { display: none !important; } }

  #ptb {
    position: fixed; top: 0; left: 0; right: 0;
    background: ${TEAL}; color: #fff;
    padding: 7px 16px; display: flex; justify-content: space-between; align-items: center;
    z-index: 9999; font-family: Arial, sans-serif;
  }

  /* Two PVC cards side by side */
  .cards-wrap { display: flex; flex-direction: row; gap: 8mm; align-items: flex-start; padding: 4mm; }

  /* CR80 PVC credit-card standard: 85.6mm × 54mm */
  .pvc-card {
    width: 85.6mm; height: 54mm;
    background: #fff;
    border: 2px solid ${TEAL};
    border-radius: 3.5mm;
    display: flex; flex-direction: column;
    overflow: hidden;
    box-shadow: 0 3px 12px rgba(0,0,0,0.2);
  }

  /* Header stripe */
  .card-hdr {
    background: ${TEAL};
    display: flex; align-items: center; gap: 2mm;
    padding: 1.5mm 2.5mm;
    flex-shrink: 0;
  }
  .logo { height: 10mm; width: auto; }
  .clinic-name { font-size: 7pt; font-weight: bold; color: #fff; line-height: 1.2; }
  .card-sub { font-size: 4.5pt; color: rgba(255,255,255,0.88); margin-top: 0.3mm; }

  /* Card body: info left + barcodes right */
  .card-body { flex: 1; display: flex; overflow: hidden; }

  .info-col {
    flex: 1; min-width: 0;
    padding: 2mm 1mm 1mm 2mm;
    display: flex; flex-direction: column; gap: 0.7mm;
  }

  .pt-name {
    font-size: 8.5pt; font-weight: bold; color: #111;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-bottom: 1mm;
  }

  .info-row { display: flex; gap: 1mm; align-items: baseline; font-size: 5.5pt; line-height: 1.4; }
  .lbl { color: #888; min-width: 13mm; flex-shrink: 0; }
  .val { color: #1a1a1a; font-weight: 600; }
  .id-val { color: ${TEAL}; font-size: 5pt; letter-spacing: 0.03em; }

  /* Barcode column */
  .bc-col {
    width: 34mm; flex-shrink: 0;
    border-left: 1px dashed #ddd;
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    gap: 2mm; padding: 1.5mm;
  }

  .bc-item { text-align: center; width: 100%; }
  .bc-lbl { font-size: 4.5pt; color: ${TEAL}; font-weight: bold; text-transform: uppercase; margin-bottom: 0.5mm; }
  .bc-item svg { width: 100%; height: auto; max-height: 13mm; }

  /* Footer strip */
  .card-footer {
    flex-shrink: 0;
    background: #f5f5f5;
    border-top: 1px solid #e0e0e0;
    display: flex; justify-content: space-between;
    padding: 0.8mm 2.5mm;
    font-size: 4.5pt; color: #888;
  }
</style>
</head>
<body>

<div id="ptb">
  <span style="font-weight:700;font-size:9pt">Patient Card — ${patientId}</span>
  <div style="display:flex;gap:8px">
    <button onclick="window.print()" style="background:#fff;color:${TEAL};border:none;padding:5px 14px;border-radius:4px;font-weight:700;cursor:pointer;font-size:8.5pt">Print</button>
    <button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.85);border:1px solid rgba(255,255,255,0.4);padding:5px 9px;border-radius:4px;font-weight:700;cursor:pointer;font-size:8.5pt">&#10005;</button>
  </div>
</div>

<div class="cards-wrap">
  ${patientCard}
  ${spouseCard}
</div>

${barcodeScript}
</body>
</html>`;

  const win = window.open('', '_blank', `width=${hasSpouse ? 920 : 500},height=440,toolbar=0,menubar=0`);
  win.document.write(cardHtml);
  win.document.close();
}
